import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

export enum AppMode {
  DEMO = 'DEMO',
  WEB = 'WEB',
}

export type ModeContext = {
  demoEnabled?: boolean;
  // future flags go here
};

export type ModeRule = {
  match: (ctx: ModeContext) => boolean;
  mode: AppMode;
};

@Injectable({ providedIn: 'root' })
export class AppModeService {

  private readonly rules: ModeRule[] = [
    {
      mode: AppMode.DEMO,
      match: ctx => ctx.demoEnabled!
    },
    {
      mode: AppMode.WEB,
      match: () => true
    }
  ];

  private readonly modeSubject = new BehaviorSubject<AppMode | null>(null);

  // public observable for guards
  mode$: Observable<AppMode> = this.modeSubject.asObservable().pipe(
    filter((mode): mode is AppMode => mode !== null)
  );

  constructor(private readonly router: Router) {}

  setMode(mode: AppMode): void {
    this.modeSubject.next(mode);
  }

  getMode(): AppMode | null {
    return this.modeSubject.value;
  }

  initMode(ctx: ModeContext): void {
    this.setMode(this.resolveMode(ctx));
  }

  resolveMode(ctx: ModeContext): AppMode {
    return this.rules.find(r => r.match(ctx))!.mode;
  }

  isDemoUrl(url?: string): boolean {
    const targetUrl = url ?? this.router.url;
    return targetUrl.startsWith('/demo') || targetUrl.startsWith('/secured/demo');
  }

  isDemoUrl$(): Observable<boolean> {
    return this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) =>
        this.isDemoUrl(event.urlAfterRedirects)
      ),
      startWith(this.isDemoUrl(this.router.url))
    );
  }

  isMode(mode: AppMode): boolean {
    return this.modeSubject.value === mode;
  }
}